import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { EditorHeader } from "./EditorHeader";

interface OrganizationalStructure {
  id: string;
  department: string;
  headcount: number;
  key_focus_areas: string[];
  growth_plans: string;
}

export const OrganizationalStructureEditor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: departments, isLoading, refetch } = useQuery({
    queryKey: ['organizational-structure'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizational_structure')
        .select('*')
        .order('created_at');
      
      if (error) throw error;
      return data as OrganizationalStructure[];
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>, deptId: string) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const updates = {
      department: String(formData.get('department')),
      headcount: Number(formData.get('headcount')),
      key_focus_areas: String(formData.get('key_focus_areas')).split(',').map(area => area.trim()),
      growth_plans: String(formData.get('growth_plans')),
    };

    try {
      const { error } = await supabase
        .from('organizational_structure')
        .update(updates)
        .eq('id', deptId);

      if (error) throw error;

      toast.success("Department has been updated");
      refetch();
    } catch (error) {
      toast.error("Failed to update department");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    try {
      const { error } = await supabase
        .from('organizational_structure')
        .insert({
          department: 'New Department',
          headcount: 0,
          key_focus_areas: [],
          growth_plans: 'Growth plans...',
        });

      if (error) throw error;

      toast.success("New department has been added");
      refetch();
    } catch (error) {
      toast.error("Failed to add department");
    }
  };

  const handleDelete = async (deptId: string) => {
    try {
      const { error } = await supabase
        .from('organizational_structure')
        .delete()
        .eq('id', deptId);

      if (error) throw error;

      toast.success("Department has been deleted");
      refetch();
    } catch (error) {
      toast.error("Failed to delete department");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditorHeader title="Organizational Structure Editor" />
      <Button onClick={handleAdd} className="mb-4">
        <Plus className="h-4 w-4 mr-2" />
        Add Department
      </Button>

      <div className="space-y-6">
        {departments?.map((dept) => (
          <Card key={dept.id}>
            <CardContent className="pt-6">
              <form onSubmit={(e) => handleSave(e, dept.id)} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Department Name</label>
                  <Input
                    name="department"
                    defaultValue={dept.department}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Headcount</label>
                  <Input
                    name="headcount"
                    type="number"
                    defaultValue={dept.headcount}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Key Focus Areas (comma-separated)</label>
                  <Input
                    name="key_focus_areas"
                    defaultValue={dept.key_focus_areas.join(', ')}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Growth Plans</label>
                  <Textarea
                    name="growth_plans"
                    defaultValue={dept.growth_plans}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div className="flex justify-between">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="destructive"
                    onClick={() => handleDelete(dept.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};